/* Isolated component: no global click handlers and no changes to product cards. */
if (!customElements.get('bb-editorial-menu')) {
  customElements.define('bb-editorial-menu', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('[data-bb-open]');
      this.mobile = window.matchMedia('(max-width: 989px)');
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (button?.hasAttribute('data-bb-open')) this.open();
        else if (button?.hasAttribute('data-bb-close')) this.dialog.close();
        else if (button?.hasAttribute('data-bb-back')) this.back();
        else if (button?.hasAttribute('data-bb-root')) this.selectRoot(button, true);
        else if (button?.hasAttribute('data-bb-child')) this.selectChild(button, true);
        else if (button?.hasAttribute('data-bb-letter')) {
          const panel = button.closest('[data-bb-panel]');
          panel.dataset.letter = button.dataset.bbLetter;
          panel.querySelectorAll('[data-bb-letter]').forEach(b => b.setAttribute('aria-pressed', String(b === button)));
          this.filter(panel);
        }
        // A click on the modal backdrop has the dialog as its target.
        if (event.target === this.dialog) {
          const r = this.dialog.getBoundingClientRect();
          if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.dialog.close();
        }
      }, options);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-bb-search]')) this.filter(event.target.closest('[data-bb-panel]'));
      }, options);
      this.dialog.addEventListener('close', () => this.cleanup(), options);
      window.addEventListener('resize', () => {
        if (!this.dialog.open) return;
        this.position();
        if (!this.mobile.matches && !this.rootButton) {
          const first = this.querySelector('[data-bb-root]');
          if (first) this.selectRoot(first, false);
        }
      }, options);
    }
    position() {
      const header = this.closest('.section-header') || this.closest('header');
      const bottom = header ? header.getBoundingClientRect().bottom : 90;
      this.dialog.style.setProperty('--bb-nav-top', Math.max(0, Math.min(bottom, window.innerHeight - 180)) + 'px');
    }
    open() {
      if (this.dialog.open) return;
      this.position();
      this.dialog.dataset.level = '1';
      this.rootButton = null;
      this.childButton = null;
      this.querySelectorAll('[data-bb-panel], [data-bb-detail]').forEach(p => p.hidden = true);
      this.querySelectorAll('[data-bb-root], [data-bb-child]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      this.dialog.setAttribute('data-no-detail', '');
      if (!this.mobile.matches) {
        const first = this.querySelector('[data-bb-root]');
        if (first) this.selectRoot(first, false);
      }
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.opener.setAttribute('aria-expanded', 'true');
      this.querySelector('[data-bb-close]').focus();
    }
    cleanup() {
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      this.opener.setAttribute('aria-expanded', 'false');
      if (this.isConnected) this.opener.focus();
    }
    selectRoot(button, focus) {
      this.rootButton = button;
      this.childButton = null;
      this.querySelectorAll('[data-bb-root]').forEach(b => b.setAttribute('aria-expanded', String(b === button)));
      this.querySelectorAll('[data-bb-panel]').forEach(p => p.hidden = p.dataset.bbPanel !== button.dataset.bbRoot);
      this.querySelectorAll('[data-bb-detail]').forEach(p => p.hidden = true);
      this.querySelectorAll('[data-bb-child]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      const panel = this.querySelector('[data-bb-panel="' + button.dataset.bbRoot + '"]');
      this.dialog.dataset.level = '2';
      this.dialog.setAttribute('data-no-detail', '');
      this.querySelector('[data-column="2"]').scrollTop = 0;
      if (!this.mobile.matches) {
        const first = [...panel.querySelectorAll('[data-bb-child]')].find(b => !b.closest('[data-bb-item]').hidden);
        if (first) this.selectChild(first, false);
      } else if (focus) panel.querySelector('h3').focus();
    }
    selectChild(button, focus) {
      this.childButton = button;
      this.querySelectorAll('[data-bb-child]').forEach(b => b.setAttribute('aria-expanded', String(b === button)));
      this.querySelectorAll('[data-bb-detail]').forEach(p => p.hidden = p.dataset.bbDetail !== button.dataset.bbChild);
      this.dialog.removeAttribute('data-no-detail');
      this.dialog.dataset.level = '3';
      this.querySelector('[data-column="3"]').scrollTop = 0;
      if (focus && this.mobile.matches) this.querySelector('[data-bb-detail]:not([hidden]) h3').focus();
    }
    back() {
      if (this.dialog.dataset.level === '3') {
        this.dialog.dataset.level = '2';
        this.childButton?.focus();
      } else {
        this.dialog.dataset.level = '1';
        this.rootButton?.focus();
      }
    }
    filter(panel) {
      const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
      const query = normalize(panel.querySelector('[data-bb-search]').value.trim());
      const letter = panel.dataset.letter || '';
      let count = 0;
      panel.querySelectorAll('[data-bb-item]').forEach(item => {
        const name = normalize(item.dataset.name);
        item.hidden = !name.includes(query) || (letter !== '' && !name.startsWith(letter));
        if (!item.hidden) count++;
      });
      panel.querySelector('[data-bb-empty]').hidden = count > 0;
      if (this.childButton?.closest('[data-bb-item]').hidden) {
        this.childButton.setAttribute('aria-expanded', 'false');
        this.childButton = null;
        this.querySelectorAll('[data-bb-detail]').forEach(p => p.hidden = true);
        this.dialog.setAttribute('data-no-detail', '');
        this.dialog.dataset.level = '2';
      }
    }
    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      this.cleanup();
      this.controller?.abort();
      this.controller = null;
    }
  });
}
